import { Icon } from "@pepperstone/pepper-ui-kit";
import { classNames } from "@repo/ui";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type {
  ActionMeta,
  ControlProps,
  MenuProps,
  OptionProps,
  SingleValue,
} from "react-select";
import Select, { components } from "react-select";
import ArrowDown from "@/assets/icons/arrow-down.svg";
import styles from "./DropDownMenu.module.css";
import { calculatePosition } from "./helpers";
import type { DropDownMenuProps, Position } from "./types";

export default function DropDownMenu<
  T extends {
    id: string;
  },
>({
  options,
  onSelectChange,
  trigger,
  selectedOption,
  noOptionsMessage,
  optionItemComponent,
  menuOpenDirection = "left",
  menuWidth = 384,
  menuHeight,
  "data-testid": testId,
  ...props
}: DropDownMenuProps<T>) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [position, setPosition] = useState<Position>({
    type: menuOpenDirection,
    value: 0,
  });

  const closeMenu = useCallback(() => {
    setMenuIsOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuIsOpen((prev) => !prev);
  }, []);

  const handleSelectChange = useCallback(
    (option: SingleValue<T>, action: ActionMeta<T>) => {
      if (action.action === "select-option") {
        onSelectChange(option);
        closeMenu();
      }
    },
    [onSelectChange, closeMenu]
  );

  const BaseControl = useCallback(
    (controlProps: ControlProps<T>) => (
      <components.Control {...controlProps}>
        <button
          ref={triggerRef}
          onClick={toggleMenu}
          onTouchStart={toggleMenu}
          className={styles["drop-down-trigger"]}
        >
          {trigger}
          <img
            alt="arrow-down"
            className={classNames([
              styles["drop-down-arrow"],
              menuIsOpen && styles["drop-down-arrow-open"],
            ])}
            src={ArrowDown}
          />
        </button>
      </components.Control>
    ),
    [menuIsOpen, trigger, toggleMenu]
  );

  const BaseMenu = useCallback(
    (menuProps: MenuProps<T>) => {
      // biome-ignore lint/correctness/useHookAtTopLevel: we need to focus the trigger on menu render to make the key events work
      useEffect(() => {
        triggerRef.current?.focus();
      }, []);

      return (
        <div
          ref={menuRef}
          style={{
            width: menuWidth,
            height: menuHeight,
            [position.type]: position.value,
          }}
          className={styles["drop-down-menu"]}
        >
          {menuProps.children}
        </div>
      );
    },
    [position, menuWidth, menuHeight]
  );

  const BaseOption = useCallback(
    ({ data: option, ...optionProps }: OptionProps<T>) => {
      const isSelected = selectedOption?.id === option.id;
      const renderOption = () => {
        if (optionItemComponent) return optionItemComponent(option);
        if (props.getOptionLabel) return props.getOptionLabel(option);
        return option.id;
      };

      return (
        <components.Option
          {...optionProps}
          data={option}
          selectOption={() => {
            handleSelectChange(option, { action: "select-option", option });
          }}
        >
          <div
            onClick={(e) => {
              handleSelectChange(option, { action: "select-option", option });
              e.stopPropagation();
              e.preventDefault();
            }}
            className={classNames([
              optionItemComponent
                ? styles["drop-down-option-wrapper"]
                : styles["drop-down-option"],
              (isSelected || optionProps.isFocused) && styles.selected,
            ])}
          >
            {renderOption()}
            {isSelected && <Icon icon="check-mark" color="primary" />}
          </div>
        </components.Option>
      );
    },
    [
      props.getOptionLabel,
      selectedOption,
      optionItemComponent,
      handleSelectChange,
    ]
  );

  useLayoutEffect(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const width =
      typeof menuWidth === "number"
        ? menuWidth
        : Number.parseInt(menuWidth, 10);

    const newPosition = calculatePosition(
      rect,
      width,
      window.innerWidth,
      menuOpenDirection
    );

    setPosition(newPosition);
  }, [menuOpenDirection, menuWidth]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const path = e.composedPath();

      const clickedInside = path.some((node) => node === menuRef.current);

      if (!clickedInside) {
        setMenuIsOpen(false);
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);

    return () => {
      window.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <Select
      data-testid={testId}
      unstyled
      options={options}
      getOptionValue={(option) => option.id}
      menuIsOpen={menuIsOpen}
      onChange={handleSelectChange}
      value={selectedOption}
      noOptionsMessage={noOptionsMessage}
      {...props}
      components={{
        Control: BaseControl,
        Menu: BaseMenu,
        Option: BaseOption,
        ...(props.components || {}),
      }}
      classNames={{
        ...(props.classNames || {}),
        menuList: () => styles["drop-down-menu-list"],
      }}
    />
  );
}
